/**
 * AgentOsLeaderboard.jsx
 *
 * Public top-referrers leaderboard at /agent-os/leaderboard.
 *
 * Purpose: social proof for prospects + viral driver for existing
 * users. Every workspace can earn a spot here — "look who's actively
 * referring + earning real €" creates two flywheels:
 *   1. Cold prospects see real brands using us → trust signal.
 *   2. Existing users see leaderboard → competitive nudge to share more.
 *
 * Privacy: only `brand_name` (user-supplied at onboarding, intentional
 * public surface) + signup count + view count. Never email or
 * workspace_id leak.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config";
import { Trophy, Crown, Medal, ArrowRight, Users, Eye, Coins, Bot } from "lucide-react";

const MEDAL = ["🥇", "🥈", "🥉"];

export default function AgentOsLeaderboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/agent-os/leaderboard?limit=10`);
        setData(r.data);
      } catch { /* fall through to empty state */ }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="agent-os-leaderboard-page">
      <div className="border-b border-emerald-900/30 bg-black/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
              <Bot className="w-5 h-5 text-black" />
            </div>
            <span className="font-black tracking-tight">
              TopRankPulse <span className="text-emerald-400">· Agent OS</span>
            </span>
          </a>
          <a
            href="/agent-os/onboard"
            data-testid="agent-os-leaderboard-cta"
            className="px-4 py-2 bg-emerald-400 hover:bg-emerald-300 text-black text-xs font-black uppercase tracking-widest"
          >
            Spusti svojho agenta →
          </a>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-5 border border-amber-500/40 bg-amber-950/30 text-amber-300 text-[10px] uppercase tracking-[0.3em] font-bold">
            <Trophy className="w-3 h-3" /> Top Referrers
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-4" data-testid="agent-os-leaderboard-h1">
            Kto najviac <span className="text-emerald-400">zarába</span><br />
            zdieľaním Agent OS
          </h1>
          <p className="text-base text-gray-400 max-w-2xl mx-auto">
            Top 10 brandov podľa konvertovaných signupov. 30% lifetime commission z €99 MRR.
            Každá konverzia = <span className="text-amber-300 font-bold">€29.70 / mes</span> pre referrer-a.
          </p>
        </div>

        {loading && (
          <div className="text-center text-gray-500 py-12" data-testid="agent-os-leaderboard-loading">
            Načítavam leaderboard…
          </div>
        )}

        {!loading && (!data || (data.leaderboard || []).length === 0) && (
          <div className="text-center py-12 max-w-md mx-auto" data-testid="agent-os-leaderboard-empty">
            <Trophy className="w-12 h-12 mx-auto mb-4 text-gray-700" />
            <h2 className="text-xl font-bold mb-2">Leaderboard je čistá tabuľa</h2>
            <p className="text-sm text-gray-400 mb-6">
              Buď prvý. Spusti agenta, odomkni achievement, zdieľaj a získaj #1 pozíciu.
            </p>
            <a
              href="/agent-os/onboard"
              data-testid="agent-os-leaderboard-empty-cta"
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-400 hover:bg-emerald-300 text-black font-black uppercase tracking-widest text-sm"
            >
              Spusti onboarding (60s) <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        )}

        {!loading && data && (data.leaderboard || []).length > 0 && (
          <ol className="space-y-3" data-testid="agent-os-leaderboard-list">
            {data.leaderboard.map((row, i) => {
              const isPodium = i < 3;
              const commission = (row.signups * 99 * 0.30).toFixed(2);
              return (
                <li
                  key={`${row.brand_name}-${i}`}
                  data-testid={`agent-os-leaderboard-row-${i}`}
                  className={`flex items-center gap-4 p-5 border-2 transition-colors ${
                    isPodium
                      ? "bg-gradient-to-r from-amber-950/30 to-black border-amber-700/40"
                      : "bg-black border-gray-800"
                  }`}
                >
                  <div className={`text-3xl sm:text-4xl font-black tabular-nums min-w-[60px] ${isPodium ? "text-amber-300" : "text-gray-600"}`}>
                    {MEDAL[i] || `#${row.rank}`}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-lg sm:text-xl text-white truncate">
                      {row.brand_name}
                    </div>
                    <div className="text-xs text-gray-500 flex flex-wrap gap-x-4 mt-1">
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        <span className="tabular-nums font-bold text-emerald-400">{row.signups}</span> signupov
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        <span className="tabular-nums">{row.views.toLocaleString("sk-SK")}</span> zobrazení
                      </span>
                      <span className="hidden sm:flex items-center gap-1">
                        <Medal className="w-3 h-3" />
                        {row.share_count} {row.share_count === 1 ? "share" : "shares"}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold flex items-center gap-1 justify-end">
                      <Coins className="w-3 h-3" /> commission
                    </div>
                    <div className={`text-xl sm:text-2xl font-black tabular-nums ${isPodium ? "text-amber-300" : "text-emerald-400"}`}>
                      €{commission}
                    </div>
                    <div className="text-[10px] text-gray-500">/ mes lifetime</div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        <div className="mt-16 p-8 bg-gradient-to-r from-emerald-950/40 to-black border border-emerald-700/40 text-center">
          <Crown className="w-10 h-10 mx-auto mb-3 text-amber-400" />
          <h2 className="text-2xl sm:text-3xl font-black mb-3">
            Chceš sa dostať na <span className="text-amber-400">top 3</span>?
          </h2>
          <p className="text-sm text-gray-400 mb-6 max-w-xl mx-auto">
            1. Spusti svojho prvého agenta. 2. Odomkni achievement (replies / hours saved / EUR saved).
            3. Zdieľaj badge na social media. Každý nový trial cez tvoj link = €29.70 / mes navždy.
          </p>
          <a
            href="/agent-os/onboard"
            data-testid="agent-os-leaderboard-bottom-cta"
            className="inline-flex items-center gap-2 px-7 py-3 bg-emerald-400 hover:bg-emerald-300 text-black font-black uppercase tracking-widest text-sm"
          >
            Začať teraz (zadarmo) <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </main>

      <footer className="border-t border-gray-900 py-8 text-center text-xs text-gray-600">
        © 2026 TopRankPulse · Agent OS
      </footer>
    </div>
  );
}
